import React, { useCallback, useEffect, useRef, useState } from "react";

import axiosInstance from "api/axiosInstance";
import { instructorsService } from "api/services/instructors.service";
import { homeService } from "api/services/marketing.service";
import { toursService } from "api/services/tours.service";
import "components/cms/cms.scss";
import { CMS_COMPONENT_MAP } from "components/cms";
import HomeScreenDetailItems from "components/cms/HomeScreenDetailItems";
import HomeScreenItemModal from "components/cms/HomeScreenItemModal";
import ContentHeader from "components/Layout/ContentHeader";
import Button from "components/UI/Button";
import { Input } from "components/UI/Input";
import Modal from "components/UI/Modal";
import "pages/AdminPage.scss";
import "./HomeScreenSetting.scss";
import { ITEM_FORM_FIELDS } from "utils/constant/home";
import {
  COMPONENT_TYPE_OPTIONS,
  HomeComponentLayout,
  HomeComponentType,
  LAYOUT_OPTIONS,
} from "utils/type/home";

import type { InstructorListItem } from "api/types/instructor.types";
import type {
  HomeScreenSettingComponent,
  HomeScreenSettingComponentPayload,
  HomeScreenSettingItem,
  HomeScreenSettingItemPayload,
} from "api/types/marketing.types";
import type { TourDepartureListItem, TourListItem } from "api/types/tours.types";
import type {
  IdSelectSourcesData,
  InstructorSource,
  TourDepartureSource,
} from "utils/constant/home";

/** 강사 목록 API 응답 → idSelect 소스로 변환 */
const toInstructorSources = (items: InstructorListItem[]): InstructorSource[] =>
  items.map((i) => ({
    id: i.user_id,
    title: i.name,
    description: i.introduction,
    img: i.profile_image_url ?? undefined,
    link: `/instructors/${i.user_id}`,
    brandName: i.brand_name || undefined,
    sports: (i.sports ?? [])
      .map((s) => (s as { name?: string }).name)
      .filter((s): s is string => Boolean(s)),
    certificates: undefined,
  }));

/** 투어 목록 API 응답 → 출발 회차 소스로 변환 (ALWAYS 투어의 departures + LIMITED 투어 자체) */
const toTourDepartureSources = (tours: TourListItem[]): TourDepartureSource[] => {
  const result: TourDepartureSource[] = [];
  tours.forEach((tour) => {
    if (tour.tour_type === "ALWAYS") {
      const departures: TourDepartureListItem[] = tour.departures_summary ?? [];
      departures.forEach((d) => {
        result.push({
          tourId: tour.id,
          departureId: d.id,
          title: tour.name,
          img: tour.thumbnail_image ?? undefined,
          link: `/tours/${tour.id}/departures/${d.id}`,
          region: tour.location,
          startDate: d.start_date ?? undefined,
          endDate: d.end_date ?? undefined,
          capacity: d.capacity,
          bookedCount: d.confirmed_count,
        });
      });
    } else {
      // LIMITED 투어: departure 없이 tour 자체로 노출 — departureId 생략
      result.push({
        tourId: tour.id,
        title: tour.name,
        img: tour.thumbnail_image ?? undefined,
        link: `/tours/${tour.id}`,
        region: tour.location,
        startDate: tour.start_date ?? undefined,
        endDate: tour.end_date ?? undefined,
        capacity: tour.max_participants,
        bookedCount: tour.confirmed_count,
      });
    }
  });
  return result;
};

// ─── 메인 컴포넌트 ────────────────────────────────────────
const HomeScreenSetting: React.FC = () => {
  const [components, setComponents] = useState<HomeScreenSettingComponent[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 컴포넌트 추가/수정 모달
  const [showCompModal, setShowCompModal] = useState(false);
  const [editingComponentId, setEditingComponentId] = useState<number | null>(null);
  const [compName, setCompName] = useState("");
  const [compSubtitle, setCompSubtitle] = useState("");
  const [compLink, setCompLink] = useState("");
  const [compType, setCompType] = useState<HomeComponentType>("banner");
  const [compLayout, setCompLayout] = useState<HomeComponentLayout>("grid");
  const [compSubmitting, setCompSubmitting] = useState(false);

  // 아이템 추가/수정 모달
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState<HomeScreenSettingItem | null>(null);
  const [itemSubmitting, setItemSubmitting] = useState(false);

  // idSelect 소스 (강사, 투어 회차) — 아이템 추가/수정 모달에서 사용
  const [idSelectSources, setIdSelectSources] = useState<IdSelectSourcesData>({
    instructors: [],
    tours: [],
  });
  // 강사는 한 번만 캐싱, 투어는 월 선택 시마다 재조회
  const [instructorsLoaded, setInstructorsLoaded] = useState(false);
  const [instructorsLoading, setInstructorsLoading] = useState(false);
  const [toursLoading, setToursLoading] = useState(false);
  const [lastToursMonth, setLastToursMonth] = useState<string | null>(null);

  // 추천상품 삭제 확인 모달
  const [showFeaturedDeleteModal, setShowFeaturedDeleteModal] = useState(false);
  const [featuredDeleteTarget, setFeaturedDeleteTarget] = useState<HomeScreenSettingItem | null>(
    null,
  );

  // 앱 미리보기 모달
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // 드래그 상태
  const dragIdx = useRef<number | null>(null);
  const dragOverIdx = useRef<number | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await homeService.get();
      const results = res.data.results ?? [];
      setComponents(results);
      setSelectedId(results[0]?.id ?? null);
    } catch {
      // setError("홈 화면 데이터를 불러오는 데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadInstructors = useCallback(async () => {
    if (instructorsLoaded || instructorsLoading) return;
    setInstructorsLoading(true);
    try {
      const res = await instructorsService.list();
      setIdSelectSources((prev) => ({
        ...prev,
        instructors: toInstructorSources(res.data ?? []),
      }));
      setInstructorsLoaded(true);
    } catch {
      alert("강사 목록을 불러오지 못했습니다.");
    } finally {
      setInstructorsLoading(false);
    }
  }, [instructorsLoaded, instructorsLoading]);

  const loadToursByMonth = useCallback(
    async (month: string) => {
      if (!month) return;
      // 같은 월을 연속 요청하면 무시 (이미 로딩 중이거나 직전에 받아왔다면 skip)
      if (toursLoading) return;
      if (lastToursMonth === month) return;
      setToursLoading(true);
      try {
        const res = await toursService.list({ month });
        const tourResults: TourListItem[] =
          (res.data as { results?: TourListItem[] })?.results ?? [];
        setIdSelectSources((prev) => ({
          ...prev,
          tours: toTourDepartureSources(tourResults),
        }));
        setLastToursMonth(month);
      } catch {
        alert("투어 목록을 불러오지 못했습니다.");
      } finally {
        setToursLoading(false);
      }
    },
    [toursLoading, lastToursMonth],
  );

  const selectedComponent = components.find((c) => c.id === selectedId) ?? null;
  const currentTypeOption = COMPONENT_TYPE_OPTIONS.find((o) => o.value === compType);
  const allowedLayouts = currentTypeOption?.allowedLayouts ?? LAYOUT_OPTIONS.map((o) => o.value);
  const availableLayoutOptions = LAYOUT_OPTIONS.filter((o) => allowedLayouts.includes(o.value));
  const previewTitle = compName.trim() || currentTypeOption?.label || "";
  const isEditingComponent = editingComponentId !== null;

  useEffect(() => {
    if (!allowedLayouts.includes(compLayout)) {
      setCompLayout(allowedLayouts[0]);
    }
  }, [allowedLayouts, compLayout]);

  // ─── 컴포넌트(좌측) 조작 ────────────────────────────────
  const handleDragStart = (idx: number) => {
    dragIdx.current = idx;
  };
  const handleDragEnter = (idx: number) => {
    dragOverIdx.current = idx;
  };
  const handleDragEnd = async () => {
    const from = dragIdx.current;
    const to = dragOverIdx.current;
    dragIdx.current = null;
    dragOverIdx.current = null;
    if (from === null || to === null || from === to) return;

    const reordered = [...components];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setComponents(reordered);

    // 영향받은 컴포넌트(from~to 구간)의 order 를 각각 PATCH
    const start = Math.min(from, to);
    const end = Math.max(from, to);
    const updates = reordered.slice(start, end + 1).map((c, offset) => ({
      id: c.id,
      order: start + offset,
    }));
    try {
      await Promise.all(updates.map((u) => homeService.update(u.id, { order: u.order })));
    } catch {
      alert("순서 저장에 실패했습니다.");
      fetchData();
    }
  };

  /** 컴포넌트 추가/수정 모달 폼 초기화 */
  const resetCompForm = () => {
    setCompName("");
    setCompLink("");
    setCompType("banner");
    setCompLayout(
      COMPONENT_TYPE_OPTIONS.find((o) => o.value === "banner")?.allowedLayouts[0] ?? "carousel",
    );
    setEditingComponentId(null);
  };

  const openAddComponentModal = () => {
    resetCompForm();
    setShowCompModal(true);
  };

  const openEditComponentModal = (comp: HomeScreenSettingComponent) => {
    setEditingComponentId(comp.id);
    setCompName(comp.name);
    setCompLink(comp.link ?? "");
    setCompType(comp.component_type);
    setCompLayout(comp.layout);
    setShowCompModal(true);
  };

  const closeCompModal = () => {
    setShowCompModal(false);
    resetCompForm();
  };

  const handleSubmitComponent = async () => {
    if (compSubmitting) return;
    const defaultLabel = COMPONENT_TYPE_OPTIONS.find((o) => o.value === compType)?.label ?? "";
    // community(인기게시글)는 미리보기 전용 껍데기 → link 는 항상 빈 문자열로 정규화
    const payload: HomeScreenSettingComponentPayload = {
      name: compName.trim() || defaultLabel,
      subtitle: compSubtitle.trim(),
      component_type: compType,
      layout: compLayout,
      link: compType === "community" ? "" : compLink,
      order: isEditingComponent
        ? (components.find((c) => c.id === editingComponentId)?.order ?? components.length)
        : components.length,
      is_active: true,
    };

    setCompSubmitting(true);
    try {
      if (isEditingComponent && editingComponentId !== null) {
        const res = await homeService.update(editingComponentId, payload);
        setComponents((prev) =>
          prev.map((c) => (c.id === editingComponentId ? { ...c, ...res.data } : c)),
        );
      } else {
        const res = await homeService.create({ ...payload, items: [] });
        // 백엔드 응답은 { results: [...] } 형태
        const createdRaw = res.data.results?.[0];
        if (!createdRaw) throw new Error("empty create response");
        const created = { ...createdRaw, items: createdRaw.items ?? [] };
        setComponents((prev) => [...prev, created]);
        setSelectedId(created.id);
      }
      closeCompModal();
    } catch {
      alert(isEditingComponent ? "컴포넌트 수정에 실패했습니다." : "컴포넌트 추가에 실패했습니다.");
    } finally {
      setCompSubmitting(false);
    }
  };

  const handleDeleteComponent = async (id: number) => {
    if (!window.confirm("이 컴포넌트와 하위 데이터를 모두 삭제하시겠습니까?")) return;
    try {
      await homeService.remove(id);
    } catch {
      alert("컴포넌트 삭제에 실패했습니다.");
      return;
    }

    setComponents((prev) => prev.filter((c) => c.id !== id));
    if (selectedId === id) {
      setSelectedId((_prev) => {
        const remaining = components.filter((c) => c.id !== id);
        return remaining[0]?.id ?? null;
      });
    }
  };

  // ─── 아이템(우측) 조작 ──────────────────────────────────
  const ensureIdSelectSourcesFor = (component: HomeScreenSettingComponent) => {
    const fields = ITEM_FORM_FIELDS[component.component_type] ?? [];
    // 강사는 한 번만 캐싱. 투어는 모달 안에서 월 선택에 따라 loadToursByMonth 로 재조회한다.
    if (fields.some((f) => f.type === "idSelect" && f.source === "instructors")) {
      loadInstructors();
    }
  };

  const openAddItemModal = () => {
    if (!selectedComponent) return;
    setEditingItem(null);
    setShowItemModal(true);
    ensureIdSelectSourcesFor(selectedComponent);
  };

  const openEditItemModal = (item: HomeScreenSettingItem) => {
    if (!selectedComponent) return;
    setEditingItem(item);
    setShowItemModal(true);
    ensureIdSelectSourcesFor(selectedComponent);
  };

  const closeItemModal = () => {
    setShowItemModal(false);
    setEditingItem(null);
  };

  const handleSubmitItem = async (payload: HomeScreenSettingItemPayload) => {
    if (!selectedComponent || itemSubmitting) return;

    setItemSubmitting(true);
    try {
      if (editingItem) {
        const res = await homeService.updateItem(editingItem.id, payload);
        setComponents((prev) =>
          prev.map((c) =>
            c.id === selectedComponent.id
              ? { ...c, items: c.items.map((i) => (i.id === editingItem.id ? res.data : i)) }
              : c,
          ),
        );
      } else {
        const res = await homeService.createItem(selectedComponent.id, payload);
        setComponents((prev) =>
          prev.map((c) =>
            c.id === selectedComponent.id ? { ...c, items: [...c.items, res.data] } : c,
          ),
        );
      }
      closeItemModal();
    } catch {
      alert(editingItem ? "항목 수정에 실패했습니다." : "항목 추가에 실패했습니다.");
    } finally {
      setItemSubmitting(false);
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    if (!selectedComponent) return;
    const existing = selectedComponent.items.find((i) => i.id === itemId);
    if (!existing) return;

    try {
      await homeService.removeItem(itemId);
    } catch {
      alert("항목 삭제에 실패했습니다.");
      return;
    }
    setComponents((prev) =>
      prev.map((c) =>
        c.id === selectedComponent.id ? { ...c, items: c.items.filter((i) => i.id !== itemId) } : c,
      ),
    );
  };

  /** 추천상품 삭제 모달 열기 */
  const handleOpenFeaturedDelete = (item: HomeScreenSettingItem) => {
    setFeaturedDeleteTarget(item);
    setShowFeaturedDeleteModal(true);
  };

  /** 추천상품 삭제 확인 → API 호출 */
  const handleConfirmFeaturedDelete = async () => {
    if (!featuredDeleteTarget?.product_id || !selectedComponent) return;
    try {
      await axiosInstance.patch(
        `/server/buccl_main/api/v1/products/${featuredDeleteTarget.product_id}/`,
        { is_featured: false },
      );
      try {
        await homeService.removeItem(featuredDeleteTarget.id);
      } catch {
        // 추천 해제는 성공했으므로 다음 조회 시 사라짐
      }
      setComponents((prev) =>
        prev.map((c) =>
          c.id === selectedComponent.id
            ? { ...c, items: c.items.filter((i) => i.id !== featuredDeleteTarget.id) }
            : c,
        ),
      );
    } catch {
      alert("추천상품 삭제에 실패했습니다.");
    } finally {
      setShowFeaturedDeleteModal(false);
      setFeaturedDeleteTarget(null);
    }
  };

  const handleToggleItem = async (itemId: number) => {
    if (!selectedComponent) return;
    const existing = selectedComponent.items.find((i) => i.id === itemId);
    if (!existing) return;
    const next = !existing.is_active;

    // optimistic update
    setComponents((prev) =>
      prev.map((c) =>
        c.id === selectedComponent.id
          ? {
              ...c,
              items: c.items.map((i) => (i.id === itemId ? { ...i, is_active: next } : i)),
            }
          : c,
      ),
    );

    try {
      await homeService.updateItem(itemId, { is_active: next });
    } catch {
      alert("활성/비활성 변경에 실패했습니다.");
      setComponents((prev) =>
        prev.map((c) =>
          c.id === selectedComponent.id
            ? {
                ...c,
                items: c.items.map((i) =>
                  i.id === itemId ? { ...i, is_active: existing.is_active } : i,
                ),
              }
            : c,
        ),
      );
    }
  };

  // ─── 로딩 / 에러 ───────────────────────────────────────
  if (loading) {
    return (
      <div className="admin-page">
        <ContentHeader title="홈 화면 설정" description="앱 홈 화면의 구성 요소를 관리합니다." />
        <div className="admin-page__card home-screen__loading">데이터를 불러오는 중입니다...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <ContentHeader title="홈 화면 설정" description="앱 홈 화면의 구성 요소를 관리합니다." />
        <div className="admin-page__card home-screen__error">
          <p>{error}</p>
          <Button variant="secondary" onClick={fetchData}>
            다시 시도
          </Button>
        </div>
      </div>
    );
  }

  // ─── 렌더 ──────────────────────────────────────────────
  return (
    <div className="admin-page">
      <ContentHeader
        title="홈 화면 설정"
        description="앱 홈 화면의 구성 요소를 관리합니다."
        actions={[
          <Button
            key="preview"
            variant="outline"
            onClick={() => setShowPreviewModal(true)}
            disabled={components.length === 0}
          >
            미리보기
          </Button>,
        ]}
      />

      <div className="home-screen__layout">
        {/* ─── 좌측: 컴포넌트 목록 ─── */}
        <div className="home-screen__sidebar">
          <div className="home-screen__sidebar-header">
            <h3 className="home-screen__sidebar-title">컴포넌트</h3>
            <Button size="sm" variant="outline" onClick={openAddComponentModal}>
              + 추가
            </Button>
          </div>

          <ul className="home-screen__comp-list">
            {components.map((comp, idx) => {
              const label = COMPONENT_TYPE_OPTIONS.find(
                (o) => o.value === comp.component_type,
              )?.label;
              return (
                <li
                  key={comp.id}
                  className={`home-screen__comp-item ${selectedId === comp.id ? "home-screen__comp-item--active" : ""}`}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragEnter={() => handleDragEnter(idx)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => setSelectedId(comp.id)}
                >
                  <span className="home-screen__comp-drag">⠿</span>
                  <div className="home-screen__comp-info">
                    <span className="home-screen__comp-name">{comp.name || label}</span>
                    <span className="home-screen__comp-count">
                      {label ?? comp.component_type} · {comp.items.length}개
                    </span>
                  </div>
                  <button
                    className="home-screen__comp-edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditComponentModal(comp);
                    }}
                    title="수정"
                  >
                    수정
                  </button>
                  <button
                    className="home-screen__comp-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteComponent(comp.id);
                    }}
                    title="삭제"
                  >
                    ✕
                  </button>
                </li>
              );
            })}
          </ul>

          {components.length === 0 && (
            <p className="home-screen__comp-empty">컴포넌트가 없습니다. 추가해주세요.</p>
          )}
        </div>

        {/* ─── 우측: 선택된 컴포넌트 상세 ─── */}
        <div className="home-screen__detail">
          {selectedComponent ? (
            (() => {
              // 인기게시글: 어드민이 항목을 관리하지 않고 API 로 실시간 노출 → 미리보기 전용
              const isCommunity = selectedComponent.component_type === "community";
              const CommunityPreview = CMS_COMPONENT_MAP.community;
              return (
                <>
                  <div className="home-screen__detail-header">
                    <div className="home-screen__detail-title-wrap">
                      <div>
                        <h3 className="home-screen__detail-title">{selectedComponent.name}</h3>
                        <p className="home-screen__detail-count">
                          {
                            COMPONENT_TYPE_OPTIONS.find(
                              (o) => o.value === selectedComponent.component_type,
                            )?.label
                          }{" "}
                          ·{" "}
                          {LAYOUT_OPTIONS.find((o) => o.value === selectedComponent.layout)?.label}
                          {isCommunity
                            ? " · 인기게시글 API 실시간 노출 (미리보기 전용)"
                            : ` · ${selectedComponent.items.length}개 항목`}
                        </p>
                      </div>
                    </div>
                    {!isCommunity && (
                      <Button size="sm" variant="outline" onClick={openAddItemModal}>
                        + 항목 추가
                      </Button>
                    )}
                  </div>

                  {isCommunity ? (
                    <div className="home-screen__preview-wrap">
                      <CommunityPreview
                        title={selectedComponent.name}
                        layout={selectedComponent.layout}
                      />
                    </div>
                  ) : (
                    <HomeScreenDetailItems
                      component={selectedComponent}
                      onToggleItem={handleToggleItem}
                      onDeleteItem={handleDeleteItem}
                      onEditItem={openEditItemModal}
                      onOpenFeaturedDelete={handleOpenFeaturedDelete}
                    />
                  )}
                </>
              );
            })()
          ) : (
            <div className="home-screen__detail-empty">
              <p>좌측에서 컴포넌트를 선택해주세요.</p>
            </div>
          )}
        </div>
      </div>

      {/* ─── 컴포넌트 추가/수정 모달 ─── */}
      <Modal
        open={showCompModal}
        title={isEditingComponent ? "컴포넌트 수정" : "컴포넌트 추가"}
        onClose={closeCompModal}
        closeOnOverlayClick={false}
        width="640px"
        footer={
          <>
            <Button variant="secondary" onClick={closeCompModal}>
              취소
            </Button>
            <Button variant="primary" onClick={handleSubmitComponent} disabled={compSubmitting}>
              {compSubmitting ? "저장 중..." : isEditingComponent ? "저장" : "추가"}
            </Button>
          </>
        }
      >
        <div className="home-screen__add-form">
          <div className="home-screen__add-field">
            <label className="home-screen__add-label">컴포넌트 타입</label>
            <div className="home-screen__type-grid">
              {COMPONENT_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`home-screen__type-option ${compType === opt.value ? "home-screen__type-option--selected" : ""}`}
                  onClick={() => setCompType(opt.value)}
                >
                  <span className="home-screen__type-option-label">{opt.label}</span>
                  <span className="home-screen__type-option-desc">{opt.description}</span>
                </button>
              ))}
            </div>
          </div>

          <Input
            label="컴포넌트 제목"
            placeholder="앱에서 표시될 제목 입력"
            value={compName}
            onChange={(e) => setCompName(e.target.value)}
          />
          <Input
            label="컴포넌트 부제목"
            placeholder="앱에서 표시될 부제목 입력"
            value={compSubtitle}
            onChange={(e) => setCompSubtitle(e.target.value)}
          />
          {compType !== "banner" &&
            compType !== "category" &&
            compType !== "shortcut" &&
            compType !== "community" && (
              <Input
                label="컴포넌트 링크"
                placeholder="전체 보기 연결 링크 입력"
                value={compLink}
                onChange={(e) => setCompLink(e.target.value)}
              />
            )}

          <div className="home-screen__add-field">
            <label className="home-screen__add-label">레이아웃</label>
            <div className="home-screen__layout-options">
              {availableLayoutOptions.map((opt) => (
                <label
                  key={opt.value}
                  className={`home-screen__layout-option ${compLayout === opt.value ? "home-screen__layout-option--selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="layout"
                    value={opt.value}
                    checked={compLayout === opt.value}
                    onChange={() => setCompLayout(opt.value)}
                  />
                  <span className="home-screen__layout-option-label">{opt.label}</span>
                  <span className="home-screen__layout-option-desc">{opt.description}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="home-screen__add-field">
            <label className="home-screen__add-label">미리보기</label>
            {(() => {
              const PreviewComp = CMS_COMPONENT_MAP[compType];
              return (
                <div className="home-screen__preview-wrap">
                  <PreviewComp title={previewTitle} layout={compLayout} />
                </div>
              );
            })()}
          </div>
        </div>
      </Modal>

      {/* ─── 추천상품 삭제 확인 모달 ─── */}
      <Modal
        open={showFeaturedDeleteModal}
        title="추천상품 삭제"
        onClose={() => {
          setShowFeaturedDeleteModal(false);
          setFeaturedDeleteTarget(null);
        }}
        width="480px"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowFeaturedDeleteModal(false);
                setFeaturedDeleteTarget(null);
              }}
            >
              취소
            </Button>
            <Button variant="primary" onClick={handleConfirmFeaturedDelete}>
              삭제
            </Button>
          </>
        }
      >
        <p>
          메인화면 추천상품 리스트에서만 사라지며 상품데이터는 삭제되지 않습니다. 추천상품에 다시
          노출시키고 싶다면 상품 정보 &gt; 대표상품을 변경해주세요.
        </p>
      </Modal>

      {/* ─── 앱 미리보기 모달 ─── */}
      <Modal
        open={showPreviewModal}
        title="앱 미리보기"
        onClose={() => setShowPreviewModal(false)}
        width="420px"
        footer={
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            닫기
          </Button>
        }
      >
        <div className="home-screen__app-preview">
          <div className="home-screen__app-preview-frame">
            <div className="home-screen__app-preview-notch" />
            <div className="home-screen__app-preview-screen">
              {components.length === 0 ? (
                <p className="home-screen__empty">표시할 컴포넌트가 없습니다.</p>
              ) : (
                components.map((comp) => {
                  const PreviewComp = CMS_COMPONENT_MAP[comp.component_type];
                  return (
                    <div key={comp.id} className="home-screen__app-preview-section">
                      <PreviewComp title={comp.name} layout={comp.layout} items={comp.items} />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* ─── 항목 추가/수정 모달 ─── */}
      <HomeScreenItemModal
        open={showItemModal}
        selectedComponent={selectedComponent}
        editingItem={editingItem}
        idSelectSources={idSelectSources}
        idSelectLoading={instructorsLoading}
        toursLoading={toursLoading}
        submitting={itemSubmitting}
        onClose={closeItemModal}
        onSubmit={handleSubmitItem}
        onTourMonthChange={loadToursByMonth}
      />
    </div>
  );
};

export default HomeScreenSetting;
